export type TranscriptMessage = {
  role: 'user' | 'assistant'
  content: string
}

export function shareableMessages(
  messages: Array<{ role: string; content: string }>,
): TranscriptMessage[] {
  return messages.flatMap(message => {
    if (message.role !== 'user' && message.role !== 'assistant') return []
    const content = message.content.trim()
    if (!content) return []
    return [{ role: message.role, content }]
  })
}

export function formatExportStamp(when: Date): string {
  return when.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function transcriptFilename(title: string, ext: 'pdf' | 'md' | 'txt', when = new Date()): string {
  const day = when.toISOString().slice(0, 10)
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
  return `portal-assistant-${slug || 'chat'}-${day}.${ext}`
}

function roleLabel(role: TranscriptMessage['role']): string {
  return role === 'user' ? 'You' : 'Assistant'
}

export function transcriptToMarkdown(
  title: string,
  messages: TranscriptMessage[],
  when = new Date(),
): string {
  const body = messages
    .map(message => `## ${roleLabel(message.role)}\n\n${message.content}`)
    .join('\n\n')
  return `# Portal assistant\n\n**${title}**\n\nExported ${formatExportStamp(when)}\n\n${body}\n`
}

export function transcriptToPlainText(
  title: string,
  messages: TranscriptMessage[],
  when = new Date(),
): string {
  const body = messages
    .map(message => `${roleLabel(message.role)}\n${message.content}`)
    .join('\n\n')
  return `Portal assistant\n${title}\nExported ${formatExportStamp(when)}\n\n${body}\n`
}

function downloadTextFile(filename: string, mime: string, text: string) {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

async function downloadPdf(title: string, messages: TranscriptMessage[], when: Date, filename: string) {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'pt', format: 'letter' })
  const margin = 54
  const width = doc.internal.pageSize.getWidth() - margin * 2
  const pageHeight = doc.internal.pageSize.getHeight()
  let y = margin

  const write = (text: string, size: number, style: 'normal' | 'bold') => {
    doc.setFont('times', style)
    doc.setFontSize(size)
    const lines = doc.splitTextToSize(text, width) as string[]
    const lineHeight = size + 4
    for (const line of lines) {
      if (y + lineHeight > pageHeight - margin) {
        doc.addPage()
        y = margin
      }
      doc.text(line, margin, y)
      y += lineHeight
    }
  }

  write('Portal assistant', 16, 'bold')
  y += 4
  write(title, 12, 'bold')
  y += 2
  write(`Exported ${formatExportStamp(when)}`, 10, 'normal')
  y += 12

  for (const message of messages) {
    write(roleLabel(message.role), 12, 'bold')
    y += 2
    write(message.content, 11, 'normal')
    y += 10
  }

  doc.save(filename)
}

export async function downloadTranscript(
  format: 'pdf' | 'md' | 'txt',
  title: string,
  messages: TranscriptMessage[],
  when = new Date(),
): Promise<void> {
  const filename = transcriptFilename(title, format, when)
  if (format === 'pdf') {
    await downloadPdf(title, messages, when, filename)
    return
  }
  const text =
    format === 'md'
      ? transcriptToMarkdown(title, messages, when)
      : transcriptToPlainText(title, messages, when)
  downloadTextFile(filename, format === 'md' ? 'text/markdown' : 'text/plain', text)
}
